/**
 * The consolidated family weekly order view: a scrollable column of day cards
 * (Mon–Fri) plus a right rail (month calendar + cart). Every child appears in
 * each day card regardless of campus (per-campus sections keep each child's
 * own menu); the day badge merges the most actionable status across campuses.
 * Each day card navigates to the full-page day view (`/orders/family/day`)
 * for order entry; the week stepper that drives `anchorKey` lives in the page
 * header.
 *
 * On touch devices the day-card column is a horizontal carousel: the previous
 * and next weeks' columns are mounted off-screen on either side (prefetching
 * their data), so dragging reveals the neighbor immediately and releasing past
 * the threshold completes the slide before the anchor swaps underneath.
 */

import { type ReactElement, type TouchEvent, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { useFamilyOrdersQuery } from '@/api/family-orders'
import { buildFamilyAlreadyOrdered, type FamilyAlreadyOrdered } from '@/lib/family-orders/already-ordered'
import { addDaysKey, mondayOf, todayKey, weekOf } from '@/lib/school-orders/calendar'

import { AutoOrderNudge } from './auto-order-nudge'
import { acceptsParentOrders, type FamilyOrderLocation } from './columns'
import { buildMergedDayBadges, type DayBadge } from './day-badges'
import { FamilyDayCard } from './day-card'
import { FamilyMobileCartBar } from './mobile-cart-bar'
import { FamilyRightRail } from './right-rail'

const EMPTY_BADGE: DayBadge = { status: 'NONE', label: '', tone: 'neutral', deadline: null, deadlineToday: false }

/** Horizontal gutter between the current and neighboring week columns. */
const PANEL_GAP = '0.75rem'

export interface FamilyWeekOrdersViewProps {
  locations: readonly FamilyOrderLocation[]
  /** Monday key of the selected week (driven by the header week stepper). */
  anchorKey: string
  onAnchorChange: (mondayKey: string) => void
  alreadyOrdered: FamilyAlreadyOrdered
  /** Per-day status pills keyed by date (merged across campuses). */
  dayBadges: Record<string, DayBadge>
  /** Dates with at least one submitted order — green calendar markers. */
  orderedDates: ReadonlySet<string>
  onCheckout: () => void
  submitting?: boolean
  /** Fires when the day-card list starts/stops being scrolled away from the top. */
  onScrolledChange?: (scrolled: boolean) => void
}

export function FamilyWeekOrdersView({
  locations,
  anchorKey,
  onAnchorChange,
  alreadyOrdered,
  dayBadges,
  orderedDates,
  onCheckout,
  submitting,
  onScrolledChange,
}: FamilyWeekOrdersViewProps): ReactElement {
  const weekKeys = weekOf(anchorKey)
  // Does any campus in view take parent orders? With none (every campus is
  // school ordering only, PROD-1554) there is nothing to cart or check out.
  const canOrder = locations.some(acceptsParentOrders)

  // Pin the rail to the day-card column's content height so the cart bottom-
  // aligns with the last day card.
  const dayListRef = useRef<HTMLDivElement>(null)
  const [dayListHeight, setDayListHeight] = useState<number | null>(null)
  useLayoutEffect(() => {
    const el = dayListRef.current
    if (!el) {
      return
    }
    const update = (): void => setDayListHeight(el.offsetHeight)
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [weekKeys])

  // Swipe between weeks (touch devices): during a dominantly-horizontal drag
  // the whole carousel track (current column + off-screen neighbors) follows
  // the finger 1:1; releasing past the threshold completes the slide, then the
  // anchor swaps and the transform resets in the same paint (layout effect
  // below), so the neighbor that slid in simply becomes the centered column.
  // The container keeps `touch-action: pan-y` so the browser still owns
  // vertical scrolling; a drag that locks vertical never moves the track.
  // Styles are set imperatively on `dayListRef` to avoid re-rendering the
  // card lists on every touchmove.
  const drag = useRef<{ x: number; y: number; dx: number; lock: 'h' | 'v' | null } | null>(null)
  const weekAnimating = useRef(false)
  const pendingSwap = useRef(false)
  const setListStyle = (transform: string, transition: string): void => {
    const el = dayListRef.current
    if (el) {
      el.style.transition = transition
      el.style.transform = transform
    }
  }
  const handleTouchStart = (e: TouchEvent<HTMLDivElement>): void => {
    const t = e.touches[0]
    drag.current =
      !weekAnimating.current && e.touches.length === 1 && t ? { x: t.clientX, y: t.clientY, dx: 0, lock: null } : null
  }
  const handleTouchMove = (e: TouchEvent<HTMLDivElement>): void => {
    const d = drag.current
    const t = e.touches[0]
    if (!d || !t) {
      return
    }
    const dx = t.clientX - d.x
    const dy = t.clientY - d.y
    if (!d.lock) {
      if (Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy) * 1.5) {
        d.lock = 'h'
      } else if (Math.abs(dy) > 10) {
        d.lock = 'v'
      }
    }
    if (d.lock === 'h') {
      d.dx = dx
      setListStyle(`translateX(${dx}px)`, 'none')
    }
  }
  const settleBack = (): void => setListStyle('translateX(0)', 'transform 200ms ease-out')
  const handleTouchCancel = (): void => {
    if (drag.current?.lock === 'h') {
      settleBack()
    }
    drag.current = null
  }
  const handleTouchEnd = (): void => {
    const d = drag.current
    drag.current = null
    if (!d || d.lock !== 'h') {
      return
    }
    if (Math.abs(d.dx) < 56) {
      settleBack()
      return
    }
    const dir = d.dx < 0 ? 1 : -1 // swipe left → next week
    weekAnimating.current = true
    // Slide a full panel width (+ gutter) so the neighbor lands exactly centered.
    setListStyle(`translateX(calc(${-dir * 100}% ${dir === 1 ? '-' : '+'} ${PANEL_GAP}))`, 'transform 200ms ease-out')
    window.setTimeout(() => {
      pendingSwap.current = true
      onAnchorChange(addDaysKey(anchorKey, dir * 7))
    }, 200)
  }
  // Re-center the track in the same paint as the post-swipe anchor render —
  // the new week's column takes the neighbor's exact resting place, so nothing
  // visibly moves.
  useLayoutEffect(() => {
    if (pendingSwap.current) {
      pendingSwap.current = false
      weekAnimating.current = false
      setListStyle('translateX(0)', 'none')
    }
  }, [anchorKey])

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex min-h-0 flex-1 gap-3">
        {/* On mobile the scroll container's right edge is pulled to the screen
            edge (cancelling the Shell's px-4) so its scrollbar sits at the page
            edge, not hugging the cards; cards stay inset via pr-4. Desktop keeps
            the rail-adjacent pr-1. */}
        <div
          className="-mr-4 min-h-0 min-w-0 flex-1 touch-pan-y overflow-y-auto overflow-x-hidden pb-6 pr-4 lg:mr-0 lg:pr-1"
          onScroll={(e) => onScrolledChange?.(e.currentTarget.scrollTop > 4)}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchCancel}>
          <div ref={dayListRef} className="relative">
            <div aria-hidden className="absolute right-[calc(100%+0.75rem)] top-0 w-full">
              <AdjacentWeekPanel locations={locations} weekStart={addDaysKey(anchorKey, -7)} />
            </div>
            <WeekDayList
              locations={locations}
              weekKeys={weekKeys}
              alreadyOrdered={alreadyOrdered}
              dayBadges={dayBadges}
            />
            <div aria-hidden className="absolute left-[calc(100%+0.75rem)] top-0 w-full">
              <AdjacentWeekPanel locations={locations} weekStart={addDaysKey(anchorKey, 7)} />
            </div>
          </div>
        </div>
        <div className="hidden w-[288px] shrink-0 flex-col pb-6 lg:flex lg:min-h-0">
          <FamilyRightRail
            locations={locations}
            weekKeys={weekKeys}
            orderedDates={orderedDates}
            onSelectWeek={onAnchorChange}
            height={dayListHeight}
            onCheckout={onCheckout}
            submitting={submitting}
            showCart={canOrder}
          />
        </div>
      </div>
      {canOrder ? <FamilyMobileCartBar onCheckout={onCheckout} submitting={submitting} /> : null}
    </div>
  )
}

/**
 * One week's column of day cards. On the CURRENT calendar week only, the days
 * are split into "Upcoming" (today and later — shown first) and "Past"
 * (already-passed days); other weeks render the plain Mon–Fri list.
 */
function WeekDayList({
  locations,
  weekKeys,
  alreadyOrdered,
  dayBadges,
}: {
  locations: readonly FamilyOrderLocation[]
  weekKeys: string[]
  alreadyOrdered: FamilyAlreadyOrdered
  dayBadges: Record<string, DayBadge>
}): ReactElement {
  const today = todayKey()
  const isCurrentWeek = mondayOf(today) === weekKeys[0]
  const dayGroups: Array<{ label: string | null; keys: string[] }> = isCurrentWeek
    ? [
        { label: 'Upcoming', keys: weekKeys.filter((dk) => dk >= today) },
        { label: 'Past', keys: weekKeys.filter((dk) => dk < today) },
      ].filter((g) => g.keys.length > 0)
    : [{ label: null, keys: weekKeys }]
  // The nudge points at a campus that takes parent orders — never a view-only
  // one (school ordering only), whose auto-orders the server refuses.
  const nudgeLocation = locations.find(acceptsParentOrders)
  return (
    <div className="flex flex-col gap-3">
      {nudgeLocation ? <AutoOrderNudge location={nudgeLocation} /> : null}
      {dayGroups.map((group) => (
        <div key={group.label ?? 'week'} className="flex flex-col gap-3">
          {group.label ? (
            <div className="pt-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">{group.label}</div>
          ) : null}
          {group.keys.map((dateKey) => (
            <FamilyDayCard
              key={dateKey}
              locations={locations}
              dateKey={dateKey}
              alreadyOrdered={alreadyOrdered}
              badge={dayBadges[dateKey] ?? EMPTY_BADGE}
              href={`/orders/family/day?date=${dateKey}`}
            />
          ))}
        </div>
      ))}
    </div>
  )
}

/**
 * A neighboring week's column, mounted off-screen so a swipe reveals it
 * mid-drag. Fetches its own orders + menu previews — the same query keys the
 * page uses, so by the time a swipe lands the swapped-in week renders from
 * cache with no refetch flash.
 */
function AdjacentWeekPanel({
  locations,
  weekStart,
}: {
  locations: readonly FamilyOrderLocation[]
  weekStart: string
}): ReactElement {
  const weekKeys = weekOf(weekStart)
  const ordersQuery = useFamilyOrdersQuery({ weekStart })
  const orders = useMemo(() => ordersQuery.data?.orders ?? [], [ordersQuery.data])
  const alreadyOrdered = useMemo(() => buildFamilyAlreadyOrdered(orders), [orders])
  const dayBadges = useMemo(() => buildMergedDayBadges(locations, weekKeys, orders), [locations, weekKeys, orders])
  return <WeekDayList locations={locations} weekKeys={weekKeys} alreadyOrdered={alreadyOrdered} dayBadges={dayBadges} />
}
